import React, { useMemo } from "react";
import { Lock } from "lucide-react";
import { useLaboBall } from "./LaboBall";
import { computeConsensus, signatureTeaser, type LaboDraw, type WindowSpec } from "./laboStats";
import styles from "./labo.module.css";

interface Props {
  tiragesDesc: LaboDraw[];
  isPremium: boolean;
}

const TOP_K = 15;
const MAX_ROWS = 25;
const TEASER_TOP = 15;

/**
 * Outil « Signature durable » (← classeur « KENO + », logique « cheval max »
 * multi-blocs). Repère les numéros qui restent dans le top 15 sur plusieurs
 * fenêtres temporelles (récent / intermédiaire / complet) : une dominance
 * stable plutôt qu'un pic ponctuel.
 *
 * La signature multi-fenêtres n'a de sens qu'avec un historique long : sur les
 * ~50 tirages de l'accès gratuit, les fenêtres « intermédiaire » et « complet »
 * se confondent et l'échantillon est trop mince. Le gratuit reçoit donc un
 * teaser (classement de fréquence simple sur la période disponible) et la vraie
 * signature multi-horizons est réservée au Premium (historique complet).
 */
export const SignatureDurableTool: React.FC<Props> = ({ tiragesDesc, isPremium }) => {
  const Ball = useLaboBall();
  const len = tiragesDesc.length;

  const windows = useMemo<WindowSpec[]>(
    () => [
      { label: "Récent", size: Math.min(20, len) },
      { label: "Intermédiaire", size: Math.min(60, len) },
      { label: "Complet", size: len },
    ],
    [len]
  );

  const consensus = useMemo(
    () => computeConsensus(tiragesDesc, windows, TOP_K),
    [tiragesDesc, windows]
  );
  const durable = useMemo(() => consensus.filter((e) => e.inAllWindows), [consensus]);

  const teaser = useMemo(() => signatureTeaser(tiragesDesc, TEASER_TOP), [tiragesDesc]);

  // -------- Accès gratuit : teaser (classement de fréquence simple) ----------
  if (!isPremium) {
    const maxCount = teaser.length ? teaser[0].count : 0;
    return (
      <div className={styles.card}>
        <div className={styles.verdict}>
          <p className={styles.verdictSentence}>
            {teaser.length > 0 ? (
              <>
                Sur les <span className={styles.verdictAccent}>{len}</span> derniers tirages, le numéro{" "}
                <span className={styles.verdictAccent}>{teaser[0].number}</span> est le plus fréquent :{" "}
                <span className={styles.verdictAccent}>{teaser[0].count} sorties</span>.
              </>
            ) : (
              "Pas assez de tirages pour établir un classement."
            )}
          </p>
          <div className={styles.verdictPills}>
            <span className={`${styles.pill} ${styles.pillNeutral}`}>aperçu sur {len} tirages</span>
          </div>
        </div>

        <ol className={styles.rankList}>
          {teaser.map((r, i) => (
            <li key={r.number} className={styles.rankItem}>
              <span className={styles.rankPos}>{i + 1}</span>
              <Ball number={r.number} />
              <div className={styles.rankBarTrack}>
                <div className={styles.rankBarFill} style={{ width: `${maxCount ? (r.count / maxCount) * 100 : 0}%` }} />
              </div>
              <span className={styles.rankCount}>{r.count} sortie{r.count > 1 ? "s" : ""}</span>
            </li>
          ))}
        </ol>

        <div className={styles.premiumTeaser}>
          <Lock size={16} className={styles.premiumTeaserIcon} />
          <p className={styles.premiumTeaserText}>
            Ceci est un aperçu sur {len} tirages. La <strong>signature durable</strong>, c'est-à-dire les numéros
            qui dominent sur plusieurs horizons à la fois (récent, intermédiaire et historique
            complet), nécessite tout l'historique des tirages.{" "}
            <a href="/tarifs/" className={styles.verdictAccent}>Débloquer avec Premium →</a>
          </p>
        </div>
      </div>
    );
  }

  // -------- Premium : signature durable multi-fenêtres ----------------------
  const rows = consensus.slice(0, MAX_ROWS);
  return (
    <div className={styles.card}>
      <div className={styles.verdict}>
        <p className={styles.verdictSentence}>
          {durable.length > 0 ? (
            <>
              <span className={styles.verdictAccent}>{durable.length}</span> numéros gardent une
              signature durable : présents dans le top {TOP_K} des trois fenêtres.
            </>
          ) : (
            "Aucun numéro ne domine simultanément les trois fenêtres sur cette période."
          )}
        </p>
        <div className={styles.verdictPills}>
          {durable.slice(0, 6).map((e) => (
            <span key={e.number} className={`${styles.pill} ${styles.pillGreen}`}>
              N°{e.number}
            </span>
          ))}
        </div>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.numCol}>N°</th>
              <th className={styles.tdCenter}>Fenêtres top {TOP_K}</th>
              {windows.map((w) => (
                <th key={w.label} className={styles.tdRight}>
                  {w.label}
                  <br />
                  <span style={{ fontWeight: 400, textTransform: "none" }}>({w.size} tir.)</span>
                </th>
              ))}
              <th className={styles.tdCenter}>Statut</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.number}>
                <td>
                  <Ball number={e.number} />
                </td>
                <td className={styles.tdCenter}>
                  <span className={styles.windowDots} aria-label={`${e.windowsInTopK} sur ${windows.length}`}>
                    {windows.map((_, i) => (
                      <span
                        key={i}
                        className={`${styles.dot} ${e.rankByWindow[i] <= TOP_K ? styles.dotOn : ""}`}
                      />
                    ))}
                  </span>
                </td>
                {windows.map((_, i) => (
                  <td key={i} className={styles.tdRight}>
                    #{e.rankByWindow[i]}
                  </td>
                ))}
                <td className={styles.tdCenter}>
                  {e.inAllWindows ? (
                    <span className={`${styles.badge} ${styles.badgeDurable}`}>Durable</span>
                  ) : (
                    <span className={`${styles.badge} ${styles.badgeNeutre}`}>{e.windowsInTopK}/3</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className={styles.methodNote}>
        Chaque fenêtre classe les numéros par fréquence de sortie sur une profondeur d'historique
        différente. Un numéro « <strong>durable</strong> » reste dans le top {TOP_K} des trois
        fenêtres : sa présence en tête ne dépend pas de la période observée.
      </p>
    </div>
  );
};
